import React from 'react';

/** Where a caret at `at` in the raw text lands once the text is sanitized.
 *
 * Sanitizing the prefix and measuring it is exact, provided the rule maps
 * characters independently: whatever it does to the text before the caret, it
 * does the same inside the whole string. `slugify` is such a rule.
 *
 * Separate from the handler below because this is the part worth asserting on,
 * and a pure function needs no DOM to test.
 */
export const caretAfter = (
  sanitize: (value: string) => string,
  raw: string,
  at: number
): number => sanitize(raw.slice(0, at)).length;

/**
 * Apply a rewriting rule to what the user just typed, without the caret
 * jumping to the end. Returns the clean value for the caller to store.
 *
 *     const onChange = (e) => setName(sanitizeChange(e, (v) => slugify(v, MAX)));
 *
 * **Why this writes to the DOM instead of waiting for a render.**
 *
 * The obvious shape is to remember the caret and restore it in a layout effect
 * after React re-renders. That fails exactly when the rule rejects a keystroke
 * outright: type a space into `myenv` and the clean value is `myenv` — the
 * string already in state. React bails out of an update whose next state is
 * `Object.is`-equal to the current one, so there is no re-render, no layout
 * effect, and no restore. React still reconciles the input, whose DOM value is
 * now `my env`, by assigning `node.value` — and assigning `value` drops the
 * caret at the end. The one field that survived did so only because its
 * handler happened to set a spread object, which can never compare equal.
 *
 * Correcting the DOM here, inside the event, does not depend on a render
 * happening:
 *
 * * state unchanged — the node already holds the clean value, so React's
 *   `restoreStateIfNeeded` finds it equal to props and writes nothing
 * * state changed — React re-renders, finds the node already equal to the new
 *   prop, and again writes nothing
 *
 * Either way the selection we set is the one that survives.
 *
 * The one assumption: the caller stores *this* value. Storing something
 * further transformed would put props and DOM back out of step, and React
 * would rewrite the node — correctly, but with the caret at the end.
 */
export const sanitizeChange = (
  event: React.ChangeEvent<HTMLInputElement>,
  sanitize: (value: string) => string
): string => {
  const el = event.target;
  const raw = el.value;
  const clean = sanitize(raw);

  if (clean !== raw) {
    const pos = caretAfter(sanitize, raw, el.selectionStart ?? raw.length);
    el.value = clean;
    try {
      el.setSelectionRange(pos, pos);
    } catch {
      // Input types without a text selection (number, email) throw here. The
      // value is still corrected; only the caret is beyond reach.
    }
  }

  return clean;
};
